import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';

const ICONS = {
  'My Hours': '⏱️',
  'Register for Events': '📅',
  'FE Event Sign Up': '📅',
  'FE Events (Student View)': '📅',
  'Projects Directory': '📂',
  'Coursework': '📘',
  'Request FE Hours': '📝',
  'My Account': '👤',
  'User Management': '👥',
  'Schedule Builder': '🗓️',
  'Community': '🏘️',
  'My Events': '🎪',
  'Create Event': '➕',
  'Event Attendance': '✅',
  'Project Builder': '🛠️',
  'Assignment Builder': '📚',
  'Course Builder': '🏗️',
  'My Courses': '🎓',
  'LMS Roster & Progress': '🏫',
  'Preview Courses': '🎬',
  'Waiver Forms': '📄',
  'My Directory': '📝', // Clipboard with pen
  'Admin Directory': '📋',
  'Assignment Submissions Auditor': '🔍',
  'Register Students for Events': '🗓',
  'Newsletter': '📰',
  'View Posts': '📬',
  'Page Access Manager': '🔐',
  'Student Hours': '📊',
  'Course Enroll Manager': '👥',
  'Users - CSV Import': '⬇️',
  'Instructor Concentrations': '🎯',
  'Taxonomy Management': '🏷️',
  'Analytics': '📈',
};

const Home = ({ userData, navQuickLinks = [], navQuickLinksReady = true }) => {
  const navigate = useNavigate();
  const isLoggedIn = !!userData;

  const defaultAccessCard = userData?.accesscards?.find(
    (ac) => ac.accesscard_id === userData.default_accesscard_id
  );
  const role = defaultAccessCard?.role;

  const menuItems = !navQuickLinksReady ? [] : navQuickLinks;

  // If not logged in, show the landing page
  if (!isLoggedIn) {
    return (
      <div style={styles.container}>
        <div style={styles.heroSection}>
          <h1 style={styles.title}>Welcome to Acuity CTE!</h1>
          <p style={styles.heroDescription}>
            Your comprehensive platform for Career and Technical Education.
            Track hours, manage projects, and advance your CTE journey.
          </p>
          <button style={styles.getStartedButton} onClick={() => navigate('/login')}>
            Get Started
          </button>
        </div>

        <div style={styles.featuresSection}>
          <h2 style={styles.sectionTitle}>What We Offer</h2>
          <div style={styles.featuresGrid}>
            <div className="home-widget home-widget--static" style={styles.featureCard}>
              <div style={styles.featureIcon}>📅</div>
              <h3 style={styles.featureTitle}>Field Experience Events</h3>
              <p style={styles.featureDescription}>
                Register for workshops, industry visits, and hands-on learning experiences that count toward your CTE
                requirements.
              </p>
            </div>

            <div className="home-widget home-widget--static" style={styles.featureCard}>
              <div style={styles.featureIcon}>🎓</div>
              <h3 style={styles.featureTitle}>Coursework & Learning</h3>
              <p style={styles.featureDescription}>
                Access structured courses, assignments, and learning materials designed to build your technical skills and
                knowledge.
              </p>
            </div>

            <div className="home-widget home-widget--static" style={styles.featureCard}>
              <div style={styles.featureIcon}>🛠️</div>
              <h3 style={styles.featureTitle}>Projects & Assignments</h3>
              <p style={styles.featureDescription}>
                Complete hands-on projects and assignments that demonstrate your practical skills and contribute to your CTE
                portfolio.
              </p>
            </div>

            <div className="home-widget home-widget--static" style={styles.featureCard}>
              <div style={styles.featureIcon}>📝</div>
              <h3 style={styles.featureTitle}>Alternative Credit Options</h3>
              <p style={styles.featureDescription}>
                Submit external activities, work experience, and other learning opportunities for CTE credit consideration.
              </p>
            </div>
          </div>
        </div>

        <div style={styles.ctaSection}>
          <h2 style={styles.ctaTitle}>Ready to Begin Your CTE Journey?</h2>
          <p style={styles.ctaDescription}>
            Join thousands of students and professionals advancing their careers through structured learning and hands-on
            experience.
          </p>
          <button style={styles.secondaryButton} onClick={() => navigate('/login')}>
            Sign In to Your Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Welcome to Acuity CTE!</h1>
      <p style={styles.description}>
        {role ? 'Quick access to your main actions:' : 'Please log in to see your options.'}
      </p>
      {!navQuickLinksReady ? (
        <p style={styles.loadingNote}>Loading your shortcuts…</p>
      ) : menuItems.length === 0 ? (
        <p style={styles.loadingNote}>
          No quick links match your account under the current home page configurations. Adjust Page Access Manager →
          Home page quick links, or delete those rows to restore the default role-based list.
        </p>
      ) : (
        <div
          className={`home-menu-grid${menuItems.length < 10 ? ' home-menu-grid--few' : ''}`}
        >
          {menuItems.map((item, index) => (
            <button
              key={`${item.path}-${item.label}-${index}`}
              type="button"
              className="home-widget"
              style={styles.menuButton}
              onClick={() => navigate(item.path)}
            >
              <span className="home-widget__icon">
                {item.icon || ICONS[item.label] || '🔗'}
              </span>
              <span className="home-widget__label">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '1.5rem 1.25rem',
    maxWidth: '960px',
    margin: '0 auto',
    textAlign: 'center',
  },
  title: {
    fontSize: '1.65rem',
    marginBottom: '0.5rem',
    color: '#1D4E89',
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  description: {
    fontSize: '0.9rem',
    color: '#64748b',
    lineHeight: 1.5,
    marginBottom: '1.5rem',
  },
  loadingNote: {
    fontSize: '0.875rem',
    color: '#64748b',
    marginBottom: '1.5rem',
    lineHeight: 1.5,
  },
  menuButton: {
    color: '#1D4E89',
    fontWeight: 500,
  },
  heroSection: {
    padding: '3rem 1.5rem',
    background: 'linear-gradient(135deg, #f7fafd 0%, #e6f3ff 100%)',
    borderRadius: '16px',
    marginBottom: '3rem',
    border: '1px solid #e0e7ef',
  },
  heroDescription: {
    fontSize: '1.1rem',
    color: '#4a5568',
    lineHeight: 1.6,
    marginBottom: '2rem',
    maxWidth: '640px',
    margin: '0 auto 2rem auto',
  },
  getStartedButton: {
    background: 'linear-gradient(135deg, #1D4E89 0%, #2d5aa0 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 15px rgba(29,78,137,0.3)',
    outline: 'none',
  },
  featuresSection: {
    marginBottom: '3rem',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    color: '#1D4E89',
    marginBottom: '1.25rem',
    fontWeight: '600',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '8px',
    marginBottom: '1.5rem',
    justifyContent: 'center',
    maxWidth: '720px',
    margin: '0 auto 1.5rem auto',
  },
  featureCard: {
    alignItems: 'flex-start',
    textAlign: 'left',
    minHeight: 'auto',
    padding: '14px 12px',
  },
  featureIcon: {
    fontSize: '1.5rem',
    marginBottom: '2px',
    display: 'block',
  },
  featureTitle: {
    fontSize: '0.85rem',
    color: '#1D4E89',
    marginBottom: '4px',
    fontWeight: '600',
    lineHeight: 1.3,
  },
  featureDescription: {
    fontSize: '0.75rem',
    color: '#64748b',
    lineHeight: 1.45,
    margin: 0,
  },
  ctaSection: {
    background: 'linear-gradient(135deg, #1D4E89 0%, #2d5aa0 100%)',
    color: 'white',
    padding: '2.25rem 1.5rem',
    borderRadius: '16px',
    marginBottom: '1.5rem',
  },
  ctaTitle: {
    fontSize: '1.4rem',
    marginBottom: '0.75rem',
    fontWeight: '600',
  },
  ctaDescription: {
    fontSize: '0.95rem',
    lineHeight: 1.5,
    marginBottom: '1.5rem',
    opacity: '0.9',
  },
  secondaryButton: {
    background: 'transparent',
    color: 'white',
    border: '2px solid white',
    borderRadius: '10px',
    padding: '0.75rem 1.75rem',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
};

export default Home;
